import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { CloudData } from 'angular-tag-cloud-module';
import { PrintDocumentService } from '../../../../print-documents/print-document.service';
import { CustomBarChartComponent } from '../../../../shared/components/custom-bar-chart/custom-bar-chart.component';
import { LineChartComponent } from '../../../../shared/components/line-chart/line-chart.component';
import { PrintFilterDataComponent } from '../../../../shared/components/print-filter-data/print-filter-data.component';
import {
  MAX_CLOUD_WORDS,
  WordCloudComponent,
} from '../../../../shared/components/word-cloud/word-cloud.component';
import { WordTableComponent } from '../../../../shared/components/word-table/word-table.component';
import {
  DocumentTypeEnum,
  ExportToPdfService,
} from '../../../../shared/services/export-to-pdf.service';
import { LoadingService } from '../../../../shared/services/loading.service';
import { ReportFilterService } from '../../../../shared/services/report-filter.service';
import { EvolutionReportService } from '../../../../shared/services/reporting-services/evolution-report.service';
import { RankingReportService } from '../../../../shared/services/reporting-services/ranking-report.service';
import { SummaryReportService } from '../../../../shared/services/reporting-services/summary-report.service';
import { WordCloudReportService } from '../../../../shared/services/reporting-services/word-cloud-report.service';
import { ChartTypeColorsEnum } from '../../../../utils/enums/chart-colors.enum';
import { IEvolutionReport } from '../../../../utils/interfaces/evolution-report';
import { IRankingReport } from '../../../../utils/interfaces/ranking-report';
import { IReportFilter } from '../../../../utils/interfaces/report-filter';
import { ISummaryReport } from '../../../../utils/interfaces/summary-report';

@Component({
  selector: 'app-management-diary',
  standalone: true,
  imports: [
    CommonModule,
    CustomBarChartComponent,
    LineChartComponent,
    WordCloudComponent,
    WordTableComponent,
    PrintFilterDataComponent,
  ],
  templateUrl: './management-diary.component.html',
  styleUrls: [
    '../summary/summary.component.scss',
    '../wordcloud/wordcloud.component.scss',
    './management-diary.component.scss',
  ],
})
export class ManagementDiaryComponent implements OnInit, OnDestroy {
  print = false;
  private readonly unsubscribe$ = new Subject();
  chartColorsEnum = ChartTypeColorsEnum;

  dataSummary!: ISummaryReport;
  dataEvolution!: IEvolutionReport;
  dataRanking: IRankingReport[] = [];
  wordcloud: CloudData[] = [];
  wordcloudChart: CloudData[] = [];
  countWordclound = 0;

  filter: IReportFilter | null = null;
  initialDate!: string;
  finalDate!: string;

  constructor(
    private readonly reportFilterService: ReportFilterService,
    private readonly summaryReportService: SummaryReportService,
    private readonly evolutionReportService: EvolutionReportService,
    private readonly wordCloudReportService: WordCloudReportService,
    private readonly rankingReportService: RankingReportService,
    private readonly loadingService: LoadingService,
    private readonly exportToPdfService: ExportToPdfService,
    private readonly printDocumentService: PrintDocumentService
  ) {
    effect(
      () => {
        this.printDocument(exportToPdfService.getDocumentType());
      },
      {
        allowSignalWrites: true,
      }
    );
    effect(
      () => {
        this.filter = reportFilterService.getFilter();
        this.getSummaryReportData();
        this.getEvolutionReport();
        this.getWordcloudData();
        this.getRanking();
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit(): void {
    this.initialDate =
      new Date(new Date().setDate(new Date().getDate() - 12))
        .toISOString()
        .split('T')[0] + 'T00:00:00.000Z';

    this.finalDate = new Date().toISOString().split('T')[0] + 'T00:00:00.000Z';
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  getSummaryReportData(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.summaryReportService
      .getSummaryReport(this.filter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.dataSummary = res.data;
        this.loadingService.closeLoading();
      });
  }

  getEvolutionReport(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.evolutionReportService
      .getEvolutionByDaysReport({
        ...this.filter,
        startDate: this.initialDate,
        finishDate: this.finalDate,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.data) {
          if (res.data.labels.length) this.dataEvolution = res.data;
        }

        setTimeout(() => {
          this.loadingService.closeLoading();
        }, 750);
      });
  }

  getRanking(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.rankingReportService
      .getRankingReport(this.filter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.data.length) {
          this.dataRanking = res.data;
        }

        setTimeout(() => {
          this.loadingService.closeLoading();
        }, 750);
      });
  }

  getWordcloudData(): void {
    if (!this.filter) return;

    setTimeout(() => {
      this.loadingService.showLoading();
    }, 0);

    this.wordCloudReportService
      .getWordCloudReport(this.filter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        res.data.wordCloud.sort((a, b) => b.weight - a.weight);
        this.wordcloud = [...res.data.wordCloud];
        this.wordcloudChart = this.wordcloud.slice(0, MAX_CLOUD_WORDS);

        this.countWordclound = 0;
        this.wordcloud.forEach((data) => {
          this.countWordclound = this.countWordclound + data.weight;
        });

        setTimeout(() => {
          this.loadingService.closeLoading();
        }, 750);
      });
  }

  printDocument(document: DocumentTypeEnum | null): void {
    if (
      !document ||
      (!this.dataSummary && !this.dataEvolution && !this.wordcloud.length)
    ) {
      this.exportToPdfService.generatePDF(null);
      return;
    }

    this.printDocumentService.printDocument(DocumentTypeEnum.managementDiary, {
      wordcloud: this.wordcloud,
      dataRanking: this.dataRanking,
      dataEvolution: this.dataEvolution,
      dataSummary: this.dataSummary,
      filter: this.filter,
    });
    this.exportToPdfService.generatePDF(null);
  }
}
